import {
  DownOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Spin,
  Table,
  Typography,
  Tag,
  Input,
  Space,
  InputRef,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState, useRef } from 'react';
import CustomerResponse, { DebtPaymentDto } from '../../model/Customer.model';
import CustomerService from '../../service/customer-service/Customer.service';
import ExcelService from '../../service/excel-service/Excel.service';
import { formatDate, formatMoney } from '../../utils/formatters';
import type { FilterConfirmProps } from 'antd/es/table/interface';
const { Title, Text } = Typography;

interface ExpandedPaymentProps {
  debtPayments: DebtPaymentDto[] | undefined;
}

// Component for expanded row content
const ExpandedPayments: React.FC<ExpandedPaymentProps> = ({ debtPayments }) => {
  const columns: ColumnsType<DebtPaymentDto> = [
    {
      title: 'Mã Thanh Toán',
      dataIndex: 'paymentId',
      key: 'paymentId',
    },
    {
      title: 'Ngày',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => formatDate(date),
    },
    {
      title: 'Trạng Thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color;
        let text;

        switch (status) {
          case 'COMPLETED':
            color = 'success';
            text = 'Đã hoàn thành';
            break;
          case 'PENDING':
            color = 'warning';
            text = 'Đang chờ xử lý';
            break;
          case 'OVERDUE':
            color = 'error';
            text = 'Quá hạn';
            break;
          case 'PAID':
            color = 'processing';
            text = 'Đã thanh toán';
            break;
          case 'ABANDONED':
            color = 'default';
            text = 'Đã hủy';
            break;
          default:
            color = 'default';
            text = status;
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Số Tiền',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount) => formatMoney(amount),
    },
  ];

  return (
    <div className="bg-gray-50 p-4 rounded-md">
      <Title level={5} className="mb-3">
        Chi Tiết Khoản Nợ
      </Title>
      <Table
        columns={columns}
        dataSource={debtPayments}
        rowKey="paymentId"
        pagination={false}
        size="small"
        className="border border-gray-200 rounded"
      />
    </div>
  );
};

const DeptInMonth: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // State cho search
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await CustomerService.getCustomersWithDebt();
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching debt data:', err);
      setError('Không thể tải dữ liệu nợ. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate total unpaid amount
  const totalUnpaidAmount = customers.reduce(
    (total, customer) => total + (customer.unpaidAmount || 0),
    0
  );

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (
    dataIndex: string
  ): ColumnsType<CustomerResponse>[0] => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Tìm kiếm`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Lọc
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const fieldValue = record[dataIndex as keyof CustomerResponse];
      return fieldValue
        ? fieldValue
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns: ColumnsType<CustomerResponse> = [
    {
      title: 'Tên Khách Hàng',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Liên Hệ',
      dataIndex: 'contactInfo',
      key: 'contactInfo',
      ...getColumnSearchProps('contactInfo'),
    },
    {
      title: 'Số CCCD',
      dataIndex: 'identificationNumber',
      key: 'identificationNumber',
      ...getColumnSearchProps('identificationNumber'),
    },
    {
      title: 'Số Tiền Chưa Thanh Toán',
      dataIndex: 'unpaidAmount',
      key: 'unpaidAmount',
      align: 'right',
      render: (amount) => formatMoney(amount),
    },
  ];

  // Hàm xử lý xuất Excel
  // const handleExportExcel = async () => {
  //   try {
  //     setExporting(true);
  //     await ExcelService.exportCurrentMonthDebtToExcel();
  //     setExporting(false);
  //   } catch (err) {
  //     console.error('Error exporting to Excel:', err);
  //     setExporting(false);
  //   }
  // };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Title level={3}>Nợ Tháng Hiện Tại</Title>
        <div className="flex gap-2">
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            // onClick={handleExportExcel}
            loading={exporting}
            className="bg-green-600"
          >
            Xuất Excel
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
            className="bg-blue-500"
          >
            Làm Mới
          </Button>
        </div>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon className="mb-4" />
      )}

      <Card className="mb-4 bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Text className="text-gray-700 font-medium">
              Tổng Số Khách Hàng Có Nợ:
            </Text>{' '}
            <Text>{customers.length}</Text>
          </div>
          <div>
            <Text className="text-gray-700 font-medium">
              Tổng Số Tiền Chưa Thanh Toán:
            </Text>{' '}
            <Text>{formatMoney(totalUnpaidAmount)}</Text>
          </div>
        </div>
      </Card>

      <Spin spinning={loading} tip="Đang tải dữ liệu...">
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="id"
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedPayments debtPayments={record.debtPayments} />
            ),
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <DownOutlined
                  onClick={(e) => onExpand(record, e)}
                  className="text-blue-500 cursor-pointer"
                />
              ) : (
                <RightOutlined
                  onClick={(e) => onExpand(record, e)}
                  className="text-blue-500 cursor-pointer"
                />
              ),
          }}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: 'Không tìm thấy khách hàng có nợ trong tháng hiện tại',
          }}
          className="border border-gray-200 rounded"
        />
      </Spin>
    </div>
  );
};

export default DeptInMonth;
